"use client";

import { useRef, useEffect } from "react";

/**
 * Client component that adds copy buttons to all code blocks
 * inside a content container. Uses a MutationObserver to handle
 * dynamic content changes without re-running on every render.
 */
export function CodeBlockEnhancer({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    if (!ref.current) return;

    function enhance() {
      if (!ref.current) return;
      const wrappers = ref.current!.querySelectorAll(".code-block-wrapper:not([data-enhanced])");
      wrappers.forEach((wrapper) => {
        wrapper.setAttribute("data-enhanced", "true");

        const btn = document.createElement("button");
        btn.className = "code-copy-btn";
        btn.textContent = "Copy";
        btn.type = "button";
        btn.setAttribute("aria-label", "Copy code to clipboard");

        btn.addEventListener("click", async () => {
          const code = wrapper.querySelector("code");
          if (!code) return;
          const text = code.textContent || "";
          try {
            await navigator.clipboard.writeText(text);
            btn.textContent = "Copied!";
            btn.setAttribute("aria-live", "polite");
            btn.style.color = "var(--success, #10b981)";
            setTimeout(() => {
              btn.textContent = "Copy";
              btn.removeAttribute("aria-live");
              btn.style.color = "";
            }, 2000);
          } catch {
            btn.textContent = "Failed";
            setTimeout(() => { btn.textContent = "Copy"; }, 2000);
          }
        });

        (wrapper as HTMLElement).style.position = "relative";
        wrapper.appendChild(btn);
      });
    }

    // Initial enhancement
    enhance();

    // Watch for new code blocks being added (e.g. after highlighting)
    observerRef.current = new MutationObserver(enhance);
    observerRef.current.observe(ref.current, { childList: true, subtree: true });

    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return <div ref={ref}>{children}</div>;
}
